/**
 * The one HTML sanitizer for chat content rendered with v-html.
 *
 * [FUS-387 / Security Backlog H1] MessageItem renders Rey answers, TAC agent messages and published
 * replies through markdown-it with html: true, then hands the result to v-html. With no sanitizer,
 * HTML in an answer — put there by a prompt injection hidden in DMS data, or by anything else that
 * reaches message content — ran as script in Fusion's origin every time the thread was opened.
 *
 * html: true stays on because the client itself injects markup before rendering (replaceVizChart's
 * chart containers in published replies). Instead, the FINAL html — after markdown-it and after
 * MessageItem's own table / copy-code / citation wrappers — goes through DOMPurify:
 *   - HTML profile only (no SVG or MathML), and no style, form or form-control elements.
 *   - Links and media only over http, https or mailto (plus relative and anchor links).
 *   - class, id, data-*, aria-*, role, tabindex and title survive: the table download button,
 *     copy-code button, citation tooltip and chart canvases all depend on them.
 *   - Inline style survives ONLY with the exact values replaceVizChart writes on its chart
 *     containers; any other style attribute is dropped, so an answer cannot overlay the page.
 *   - Every link that opens a new tab gets rel="noopener noreferrer".
 *   - [FUS-616] No media elements (img, picture, source, video, audio, track). The browser fetches their
 *     src the moment the message renders, with no click, so a prompt injection could put the turn's data
 *     in that URL and send it to any host. Attachment and avatar images are bound in MessageItem's
 *     template (through safeUrl), never in this html, so nothing legitimate is lost.
 */
import DOMPurify from 'dompurify';

// DOMPurify's default URI pattern, narrowed to http, https and mailto. The two trailing branches
// keep relative URLs ("/path", "#anchor", "page.html") and reject any other scheme.
const ALLOWED_URI_REGEXP = /^(?:(?:https?|mailto):|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i;

// The only inline styles the client writes into message html (MessageItem.replaceVizChart).
const ALLOWED_INLINE_STYLES = new Set([
  'display: none;',
  'display: none; width: 100%; height: 400px; position: relative;',
]);

const CONFIG = {
  USE_PROFILES: { html: true },
  FORBID_TAGS: ['style', 'form', 'input', 'button', 'textarea', 'select', 'option',
    'img', 'picture', 'source', 'video', 'audio', 'track'],
  ADD_ATTR: ['target'],
  ALLOWED_URI_REGEXP,
};

let purifier = null;

function getPurifier() {
  if (purifier) return purifier;
  purifier = DOMPurify(window);
  purifier.addHook('uponSanitizeAttribute', (node, data) => {
    if (data.attrName === 'style' && !ALLOWED_INLINE_STYLES.has((data.attrValue || '').trim())) {
      data.keepAttr = false;
    }
  });
  purifier.addHook('afterSanitizeAttributes', node => {
    if (node.tagName === 'A' && node.hasAttribute('target')) {
      node.setAttribute('target', '_blank');
      node.setAttribute('rel', 'noopener noreferrer');
    }
  });
  return purifier;
}

/**
 * Returns html that is safe to hand to v-html. Never throws: on an empty or non-string input it
 * returns an empty string.
 */
export function sanitizeHtml(html) {
  if (typeof html !== 'string' || html.length === 0) return '';
  return getPurifier().sanitize(html, CONFIG);
}

// Characters browsers drop from a URL before reading its scheme (DOMPurify's ATTR_WHITESPACE), so
// " javascript:" and "java\tscript:" are judged as "javascript:".
const URL_IGNORED_CHARS = /[\u0000-\u0020\u00A0\u1680\u180E\u2000-\u2029\u205F\u3000]/g;

/**
 * [FUS-387 / Security Backlog, scan #134 and #125] For URLs bound straight to :href / :src (citation
 * source links, file and image attachments), which never pass through sanitizeHtml. Vue does not
 * block javascript: in a bound href. Returns the URL when it is http, https, mailto or relative,
 * and null otherwise, so Vue leaves the attribute off.
 */
export function safeUrl(url) {
  if (typeof url !== 'string' || url.length === 0) return null;
  return ALLOWED_URI_REGEXP.test(url.replace(URL_IGNORED_CHARS, '')) ? url : null;
}
